import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {Button, Dropdown, Tabs} from 'antd';
import {EllipsisOutlined} from '@ant-design/icons';
import IconWrapper from '../../../IconWrapper';
import ModalWithIcon from '../../../modals/ModalWithIcon';
import BudgetDetails from '../../BudgetActiveDetailsPage/BudgetDetails';
import EditBudgetModal from '../../BudgetActiveDetailsPage/EditBudgetModal';
import {LockIcon, TeamsIcon, UnlockIcon} from '../../../../icons';
import {colorsStyles} from '../../../../utils/styles';
import {budgetStatusConstants, DEFAULT_MODAL_PROPS} from '../../../../constants';
import {budgetActions} from '../../../../state/actions/budget.actions';
import {alertActions} from '../../../../state/actions/alert.actions';

const BudgetActiveTabs = ({
  ...rest
}) => {
  const [t] = useTranslation(['main', 'budgets']);
  const budget = useSelector(state => state?.budget?.budgetRequest);
  const [menuModalProps, setMenuModalProps] = useState({ ...DEFAULT_MODAL_PROPS, key: null });
  const [editBudgetModalProps, setEditBudgetModalProps] = useState(DEFAULT_MODAL_PROPS);

  const trans = (key) => t(`budgets:modal.${key}`);

  const menuModalAdditionalProps = useMemo(() => {
    return {
      close: {
        contentDescription: trans('closeBudget.content.description'),
        contentTitle: trans('closeBudget.content.title'),
        icon: <LockIcon />,
        okText: t('close'),
        title: trans('closeBudget.title'),
      },
      open: {
        contentDescription: trans('openBudget.content.description'),
        contentTitle: trans('openBudget.content.title'),
        icon: <UnlockIcon />,
        okText: t('open'),
        title: trans('openBudget.title'),
      }
    }[menuModalProps?.key] || {};
  }, [menuModalProps?.key, t]); // eslint-disable-line react-hooks/exhaustive-deps


  const dispatch = useDispatch();

  const isAvailableToClose = [budgetStatusConstants.OVER_BUDGET, budgetStatusConstants.ACTIVE].includes(budget?.status);

  const isAvailableToOpen = budget?.status === budgetStatusConstants.CLOSED;

  const items = [
    {
      label: t('details'),
      key: 'details',
      children: <BudgetDetails budget={budget} />,
    },
    // {
    //   label: t('comments'),
    //   key: 'comments',
    //   disabled: true,
    // },
    // {
    //   label: t('documents'),
    //   key: 'documents',
    //   disabled: true
    // },
  ];

  const handleBudgetAction = (action, successMessage) => ({ successCallback, errorCallback } = {}) => {
    if (!budget?.id) return;
    setMenuModalProps(prevState => ({...prevState, loading: true}));

    dispatch(
      action({
        id: budget.id,
        successCallback: (response) => {
          handleCloseMenuModal();
          alertActions.success(successMessage);
          successCallback?.(response);
        },
        errorCallback: (response) => {
          setMenuModalProps(prevState => ({
            ...prevState,
            loading: false,
            open: true,
          }));
          errorCallback?.(response);
        },
      })
    );
  };

  const handleCloseBudget = handleBudgetAction(
    budgetActions.closeBudgetRequest,
    t('budgets:alerts.budgetClosed')
  );

  const handleOpenBudget = handleBudgetAction(
    budgetActions.openBudgetRequest,
    t('budgets:alerts.budgetReopened')
  );

  const handleMenuClick = ({key}) => {
    if (['close', 'open'].includes(key)) {
      setMenuModalProps({...DEFAULT_MODAL_PROPS, open: true, key});
    }
    if (key === 'add-collaborators') {
      setEditBudgetModalProps({...DEFAULT_MODAL_PROPS, open: true});
    }
  }

  const handleCloseMenuModal = () => setMenuModalProps({...menuModalProps, loading: false, open: false});

  const handleCloseEditBudgetModal = () => setEditBudgetModalProps({...editBudgetModalProps, loading: false, open: false});

  const handleOkMenuModal = (props) => {
    const key = menuModalProps?.key;
    ({
      close: handleCloseBudget,
      open: handleOpenBudget,
    }[key])?.(props);
  }

  const handleOkEditBudgetModal = ({ data, successCallback, errorCallback } = {}) => {
    if (!budget?.id) return;
    setEditBudgetModalProps(prevState => ({...prevState, loading: true}));

    dispatch(
      budgetActions.updateBudgetRequest({
        id: budget.id,
        data,
        successCallback: (response) => {
          handleCloseEditBudgetModal();
          successCallback?.(response);
        },
        errorCallback: (response) => {
          setEditBudgetModalProps(prevState => ({
            ...prevState,
            loading: false,
            open: true,
          }));
          errorCallback?.(response);
        },
      })
    );
  }

  const renderIcon = (icon) => <IconWrapper size={16} color={colorsStyles.neutral_7}>{icon}</IconWrapper>;

  const menuProps = {
    onClick: handleMenuClick,
    items: [
      {
        label: `${t('add')} ${t('collaborators').toLowerCase()}`,
        key: 'add-collaborators',
        icon: renderIcon(<TeamsIcon />),
      },
      isAvailableToClose && {
        label: t('close'),
        key: 'close',
        icon: renderIcon(<LockIcon />),
      },
      isAvailableToOpen && {
        label: t('open'),
        key: 'open',
        icon: renderIcon(<UnlockIcon />),
      },
    ].filter(Boolean),
  };

  const tabBarExtraContent = (
    <Dropdown menu={menuProps} placement='bottomRight'>
      <Button size='middle' icon={<EllipsisOutlined />} />
    </Dropdown>
  );

  return (
    <>
      <Tabs
        defaultActiveKey='details'
        items={items}
        tabBarExtraContent={tabBarExtraContent}
        {...rest}
      />

      <ModalWithIcon
        {...menuModalAdditionalProps}
        loading={menuModalProps.loading}
        open={menuModalProps.open}
        handleCancel={handleCloseMenuModal}
        handleOk={handleOkMenuModal}
      />

      <EditBudgetModal
        {...editBudgetModalProps}
        budget={budget}
        handleCancel={handleCloseEditBudgetModal}
        handleOk={handleOkEditBudgetModal}
      />
    </>
  );
}

export default BudgetActiveTabs;
